import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowRight, MapPin } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { SERVICES, SERVICE_CATEGORIES, STATES } from "../data/siteData";
import Icon from "./Icon";

const NAV_LINKS = [
  { to: "/industries", label: "Industries" },
  { to: "/valuation-fundraising", label: "Valuation & Fundraising" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/blog", label: "Resources" },
  { to: "/about", label: "About" },
];

function MegaPanel({ children, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.18 }}
      className="absolute left-1/2 top-full z-50 mt-3 -translate-x-1/2"
      onMouseLeave={onClose}
    >
      <div className="glass-strong rounded-2xl p-6 shadow-2xl">{children}</div>
    </motion.div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-brand-sky font-display text-lg font-extrabold text-white shadow-lg shadow-brand-blue/30">
            C
          </span>
          <span className="font-display text-lg font-bold tracking-tight text-brand-navy">
            Complisure <span className="text-gradient">Counsultancy</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          <div className="relative" onMouseEnter={() => setOpenMenu("services")}>
            <button
              data-testid="nav-services-trigger"
              className="flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-medium text-brand-navy/80 transition-colors hover:bg-brand-ice hover:text-brand-blue"
            >
              Services <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <AnimatePresence>
              {openMenu === "services" && (
                <MegaPanel onClose={() => setOpenMenu(null)}>
                  <div className="grid w-[720px] grid-cols-2 gap-x-8 gap-y-5">
                    {SERVICE_CATEGORIES.map((cat) => (
                      <div key={cat}>
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-blue">{cat}</p>
                        <ul className="space-y-1">
                          {SERVICES.filter((s) => s.category === cat).map((s) => (
                            <li key={s.slug}>
                              <Link
                                to={`/services/${s.slug}`}
                                data-testid={`nav-service-${s.slug}`}
                                className="group flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-brand-ice"
                              >
                                <Icon name={s.icon} className="h-4 w-4 text-brand-blue" />
                                <span className="text-sm font-medium text-brand-navy group-hover:text-brand-blue">{s.name}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  <Link to="/services" data-testid="nav-all-services" className="mt-4 flex items-center gap-1.5 border-t border-brand-blue/10 pt-3 text-sm font-semibold text-brand-blue hover:gap-2.5 transition-all">
                    View all services A–Z <ArrowRight className="h-4 w-4" />
                  </Link>
                </MegaPanel>
              )}
            </AnimatePresence>
          </div>

          <div className="relative" onMouseEnter={() => setOpenMenu("locations")}>
            <button
              data-testid="nav-locations-trigger"
              className="flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-medium text-brand-navy/80 transition-colors hover:bg-brand-ice hover:text-brand-blue"
            >
              Locations <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <AnimatePresence>
              {openMenu === "locations" && (
                <MegaPanel onClose={() => setOpenMenu(null)}>
                  <div className="grid w-[560px] grid-cols-4 gap-4">
                    {STATES.map((st) => (
                      <Link
                        key={st.slug}
                        to={`/locations/${st.slug}`}
                        data-testid={`nav-state-${st.slug}`}
                        className="group rounded-xl p-3 transition-colors hover:bg-brand-ice"
                      >
                        <MapPin className="mb-1.5 h-4 w-4 text-brand-blue" />
                        <p className="text-sm font-semibold text-brand-navy group-hover:text-brand-blue">{st.name}</p>
                        <p className="text-xs text-brand-gray">{st.cities.length} cities</p>
                      </Link>
                    ))}
                  </div>
                </MegaPanel>
              )}
            </AnimatePresence>
          </div>

          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-brand-ice text-brand-blue" : "text-brand-navy/80 hover:bg-brand-ice hover:text-brand-blue"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-get-started-btn"
            className="btn-gradient inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-blue/25"
          >
            Get Started <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="rounded-lg p-2 text-brand-navy lg:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="glass-strong mt-2 max-h-[75vh] overflow-y-auto border-t border-white/60 lg:hidden"
          >
            <div className="space-y-1 px-4 py-4">
              <Link to="/services" data-testid="mobile-nav-services" className="block rounded-lg px-3 py-2.5 font-semibold text-brand-navy hover:bg-brand-ice">Services</Link>
              <div className="grid grid-cols-2 gap-1 pb-2 pl-3">
                {SERVICES.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} className="rounded-md px-3 py-1.5 text-sm text-brand-gray hover:text-brand-blue">{s.name.split(" &")[0]}</Link>
                ))}
              </div>
              <Link to="/locations" data-testid="mobile-nav-locations" className="block rounded-lg px-3 py-2.5 font-semibold text-brand-navy hover:bg-brand-ice">Locations</Link>
              {NAV_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="block rounded-lg px-3 py-2.5 font-semibold text-brand-navy hover:bg-brand-ice">{l.label}</Link>
              ))}
              <Link to="/contact" data-testid="mobile-nav-get-started" className="btn-gradient mt-2 block rounded-full px-5 py-3 text-center font-semibold text-white">
                Get Started
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
